import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../../data";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] } }),
};

const HEADLINE = ["Investing in the", "conscious evolution", "of humanity."];

const scrollTo = (h) => document.querySelector(h)?.scrollIntoView({ behavior: "smooth" });

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center pt-36 sm:pt-32 pb-16 overflow-hidden">
      {/* ambient golden aura */}
      <div className="pointer-events-none absolute -top-40 -right-24 w-[42rem] h-[42rem] rounded-full blur-[120px] opacity-60"
        style={{ background: "radial-gradient(circle, rgba(230,197,117,0.55), rgba(217,155,38,0.12) 60%, transparent 70%)" }} />
      <div className="pointer-events-none absolute bottom-0 -left-32 w-[34rem] h-[34rem] rounded-full blur-[120px] opacity-40"
        style={{ background: "radial-gradient(circle, rgba(200,109,34,0.35), transparent 70%)" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-center w-full">
        <div className="lg:col-span-7">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="hero-badge-integral-values"
            className="inline-flex items-center gap-2 rounded-full border border-brass/60 bg-white/50 backdrop-blur px-4 py-1.5 mb-8">
            <span className="text-xs uppercase tracking-[0.24em] text-bronze font-semibold">Integral Values · Supramental Manifestation</span>
          </motion.div>

          <h1 data-testid="hero-main-heading" className="font-serif text-ink text-[2.7rem] leading-[1.04] sm:text-6xl lg:text-[4.6rem] tracking-tight">
            {HEADLINE.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className="inline-block">
                  {i === 1 ? <span className="italic text-transparent bg-clip-text bg-gradient-to-r from-gold to-saffron">{l}</span> : l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.9 }}
            data-testid="hero-subheading" className="mt-7 max-w-xl text-lg text-stoneink leading-relaxed">
            Auro Ventures backs founders working toward the supramental manifestation — a truer, more harmonious life on earth — with patient capital and evolutionary mentorship rooted in the vision of Sri Aurobindo & The Mother.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4">
            <button data-testid="hero-cta-apply-btn" onClick={() => scrollTo("#contact-form-section")}
              className="group inline-flex items-center gap-2 rounded-full bg-ink text-sand px-7 py-3.5 font-medium hover:bg-saffron transition-colors duration-300">
              Submit Your Pitch
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button data-testid="hero-cta-manifesto-btn" onClick={() => scrollTo("#philosophy")}
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 font-medium text-ink hover:border-gold hover:text-bronze transition-colors duration-300">
              Read the Manifesto
            </button>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.3 }}
            className="mt-14 flex flex-wrap gap-10">
            {[["Conscious Capital", "Patient, values-first", "hero-stat-fund-size"], ["Integral Mentorship", "Inner & outer growth", "hero-stat-startups-count"], ["Global Fellowship", "A living network", "hero-stat-spheres"]].map(([n, l, tid]) => (
              <div key={tid} data-testid={tid} className="max-w-[10rem]">
                <div className="font-serif text-2xl sm:text-[1.75rem] text-ink leading-tight">{n}</div>
                <div className="text-xs uppercase tracking-[0.16em] text-stoneink mt-1.5">{l}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* framed spotlight image */}
        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden border border-brass/50 shadow-[0_40px_90px_-30px_rgba(140,98,33,0.5)]">
            <img src={IMAGES.heroDome} alt="Golden dome sanctuary" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/15 via-transparent to-transparent" />
          </div>
          <div className="mt-5 rounded-2xl bg-sand/85 backdrop-blur-md p-4 border border-white/40">
            <p className="font-quote italic text-ink text-[15px] leading-snug">"To work for the Divine is to cultivate perfection."</p>
            <p className="text-xs text-bronze mt-1.5 tracking-wide">— The Mother</p>
          </div>
          <div className="absolute -top-5 -left-5 w-24 h-24 rounded-full border border-gold/40 hidden sm:block" />
        </motion.div>
      </div>
    </section>
  );
}
